import _ from 'underscore';
import {
  DeterministicView,
  type DeterministicViewInstance,
  type DeterministicViewOptions,
} from './deterministic-view';

export type StatePatch<TState extends object> =
  | Partial<TState>
  | ((state: TState) => Partial<TState>);

export type ChildViewConstructor<TView extends DeterministicViewInstance> =
  new (options?: DeterministicViewOptions) => TView;

export type ChildViewCreator<TView extends DeterministicViewInstance> =
  (options?: DeterministicViewOptions) => TView;

export type ChildViewFactory<TView extends DeterministicViewInstance> =
  | ChildViewConstructor<TView>
  | ChildViewCreator<TView>;

export type ChildViewEventMap<TView extends DeterministicViewInstance> = Record<
  string,
  string | ((this: TView, ...args: unknown[]) => void)
>;

interface FactoryTrackedView<TView extends DeterministicViewInstance> {
  __factory?: ChildViewFactory<TView>;
  constructs?: ChildViewFactory<TView>;
}

/**
 * Helper function to update a view's state
 * @param {DeterministicView} view - The view to be updated
 * @param {(Function|Object)} patch -
 *    It could be either a function or an object.
 *    - If it's a funciton, new state will be patch(currentState)
 *    - If it's an object, new state will be _.defaults(patch, currentState)
 * @returns {DeterministicView} - The view being updated
 */
export function updateView<TState extends object, TView extends DeterministicViewInstance>(view: TView, patch: StatePatch<TState>): TView {
  if (_.isFunction(patch)) {
    return view.setState(patch(view.getState() as TState));
  }
  return view.setState(_.extend({}, view.getState(), patch));
}

function getFactory<TView extends DeterministicViewInstance>(view: TView & FactoryTrackedView<TView>) {
  return view.__factory || view.constructs; // eslint-disable-line
}

function setFactory<TView extends DeterministicViewInstance>(view: TView, Factory: ChildViewFactory<TView>) {
  return _.extend(view, { __factory: Factory }); // eslint-disable-line
}

/**
 * Helper function to create a child view on demond. If the child exists, return
 * it directly, otherwise create the view with the given information
 * @param {DeterministicView} view - The parent view
 * @param {string} ref - The ref of the child view
 * @param {Function} viewFactory -
 *    The factory method or constructor of the child view
 *    - If the prototype of viewFactory is instance of DeterministicView, treat
 *      it as a construstor.
 *    - Otherwise, treat it as a factory method.
 * @param {Object} options - The constructor option of the child view
 * @param {Object} events -
 *    The event handlers to be bound to the child view.
 *    - The keys of the object are child view event names.
 *    - The values of the object could be a handler function or a method name of
 *      the parent view.
 * @returns {DeterministicView} - The child view
 */
export function ensureChildView<
  TParent extends DeterministicViewInstance,
  TChild extends DeterministicViewInstance,
>(
  view: TParent,
  ref: string,
  viewFactory: ChildViewFactory<TChild>,
  options: DeterministicViewOptions = {},
  events: ChildViewEventMap<TParent> = {}
): TChild {
  let childView = view.getChild(ref) as TChild | undefined;

  if (!childView || getFactory(childView as TChild & FactoryTrackedView<TChild>) !== viewFactory) {
    if (_.isFunction(viewFactory)) {
      const callableFactory = viewFactory as ChildViewConstructor<TChild> &
        ChildViewCreator<TChild>;
      if ((viewFactory as unknown) === DeterministicView
        || callableFactory.prototype instanceof DeterministicView) {
        const ViewConstructor = callableFactory;
        childView = new ViewConstructor(options);
      } else {
        childView = callableFactory(options);
      }
    }

    if (!(childView instanceof DeterministicView)) {
      throw new Error('Invalid viewFactory');
    }

    setFactory(childView, viewFactory);

    _.each(events, (handler: ChildViewEventMap<TParent>[string], event: string) => {
      const handlerFunction = _.isString(handler) ?
        (view as TParent & Record<string, unknown>)[handler] :
        handler;
      if (!_.isFunction(handlerFunction)) {
        throw new Error('Invalid event handler');
      }
      view.listenTo(childView, event, handlerFunction as (...args: unknown[]) => void);
    });
    view.setChild(ref, childView);
  }

  return childView;
}
